import { useState } from 'react';

import styled from '@emotion/styled';
import Tab from '@mui/material/Tab';
import { LogoIcon, LogoIconVariant, TingleIcon } from '@repo/ui/icons';
import { PrivacyPolicy, TermsOfService, YouthProtectionPolicy } from '@repo/ui/policy';
import { getServiceName } from '@repo/utils';

import useCheckMediaQuery from 'hooks/useCheckMediaQuery';

import Footer from 'components/footer/Footer';

import RegulationPolicy from './components/regulation/RegulationPolicy';
import RevenuePolicy from './components/revenue/RevenuePolicy';
import { STabsWrapper } from './components/shared/style';
import TabPanel from './components/shared/TabPanel';

enum PolicyTabEnums {
  revenue = 0,
  terms = 1,
  privacy = 2,
  regulation = 3,
  youth = 4,
}

const handleTabByHash = (hash?: string) => {
  switch (hash) {
    case 'revenue':
      return PolicyTabEnums.revenue;
    case 'terms':
      return PolicyTabEnums.terms;
    case 'privacy':
      return PolicyTabEnums.privacy;
    case 'regulation':
      return PolicyTabEnums.regulation;
    case 'youth':
      return PolicyTabEnums.youth;
    default:
      return PolicyTabEnums.revenue;
  }
};

function PolicyPage() {
  const serviceName = getServiceName();
  const defaultHash = PolicyTabEnums[0];
  const hash: string = decodeURI(window.location.hash)?.split('#')[1]?.split('/')[0] ?? `#${defaultHash}`;
  const [value, setValue] = useState(handleTabByHash(hash)!);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    window.location.hash =
      PolicyTabEnums[newValue] === 'regulation' ? `regulation/service` : `${PolicyTabEnums[newValue]}`;
  };

  const { isDesktop } = useCheckMediaQuery();

  const Logo = () => {
    if (serviceName === 'tingle') {
      // reward-service 테마엔 label 그룹이 없어(sibling과 동일) 흰 policy 배경 대비용 다크 리터럴 사용
      return <TingleIcon.Logo width={123} height={23} color={'#000'} />;
    }
    return <LogoIcon variant={LogoIconVariant.BUBBLECHAT} color={'#000'} width={200} height={38} />;
  };

  return (
    <>
      <SMainWrapper>
        <Logo />
        {/* 상단 탭 */}
        <STabsArea>
          <STabsWrapper
            value={value}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
          >
            <Tab label="창작자 이용약관" />
            <Tab label="이용약관" />
            <Tab label="개인정보처리방침" />
            <Tab label="운영정책" />
            <Tab label="청소년보호정책" />
          </STabsWrapper>
        </STabsArea>

        {/* 정책 설명 영역 */}
        <SContentsArea>
          <TabPanel index={PolicyTabEnums.revenue} value={value}>
            <RevenuePolicy />
          </TabPanel>
          <TabPanel index={PolicyTabEnums.terms} value={value}>
            <TermsOfService />
          </TabPanel>
          <TabPanel index={PolicyTabEnums.privacy} value={value}>
            <PrivacyPolicy />
          </TabPanel>
          <TabPanel index={PolicyTabEnums.regulation} value={value}>
            <RegulationPolicy />
          </TabPanel>
          <TabPanel index={PolicyTabEnums.youth} value={value}>
            <YouthProtectionPolicy />
          </TabPanel>
        </SContentsArea>
      </SMainWrapper>
      <Footer isDesktop={isDesktop} />
    </>
  );
}

export default PolicyPage;

const SMainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 600px;
  min-height: 90vh;
  background-color: #ffffff;
  margin: 0 auto;
  align-items: center;

  img {
    width: 156px;
    height: 30px;
  }
`;

const STabsArea = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  margin-top: 30px;
  height: 40px;
  width: 100%;

  button {
    color: #bcbcbc;
    width: 130px;
    font-size: 14px;
    border-bottom: 2px solid #e7e7e7;
    &.Mui-selected {
      font-weight: 500;
      color: #131415;
      border-bottom: 2px solid
        ${({ theme }) => (getServiceName() === 'tingle' ? '#000000' : theme.color.static.primary)};
    }
  }
`;

const SContentsArea = styled.div`
  width: 100%;
  margin-top: 40px;
  box-sizing: border-box;
  text-align: left;

  @media screen and (max-width: 600px) {
    padding: 0 16px;
  }
`;
